<template>
  <b-card
    no-body
    class="close-deal-card h-100 mb-0"
  >
    <b-card-body class="d-flex flex-column h-100">
      <div class="d-flex align-items-start justify-content-between mb-2">
        <div class="d-flex align-items-center">
          <b-avatar
            variant="light-primary"
            size="42"
            class="mr-1"
          >
            <feather-icon
              :icon="greetingIcon"
              size="20"
            />
          </b-avatar>
          <div>
            <h5 class="mb-0 font-weight-bolder">
              {{ greeting }}, {{ name }}! {{ greetingEmoji }}
            </h5>
            <small class="text-muted">{{ subtitle }}</small>
          </div>
        </div>
        <b-link
          v-if="!isAdmin"
          class="font-small-3 text-nowrap d-flex align-items-center"
          :to="{ name: 'close-deal-lists' }"
        >
          View All
          <feather-icon
            icon="ChevronRightIcon"
            size="14"
            class="ml-25"
          />
        </b-link>
      </div>

      <template v-if="!isAdmin">
        <div
          v-if="loadingDeal"
          class="text-center py-3"
        >
          <b-spinner
            small
            variant="primary"
          />
          <div class="text-muted small mt-50">
            Loading…
          </div>
        </div>

        <template v-else-if="latestDeal">
          <div class="mb-2">
            <div class="d-flex align-items-center justify-content-between mb-25">
              <small class="text-muted">
                Latest Deal
                <template v-if="latestDeal.date">· {{ formatDate(latestDeal.date) }}</template>
              </small>
              <b-badge
                pill
                :variant="latestDeal.action === 'Rent' ? 'light-info' : 'light-success'"
              >
                {{ latestDeal.action }}
              </b-badge>
            </div>
            <h2 class="font-weight-bolder mb-50">
              ฿{{ formatCurrency(latestDeal.price) }}
            </h2>

            <div class="d-flex align-items-center font-small-3 text-muted mb-25">
              <feather-icon
                icon="HomeIcon"
                size="13"
                class="mr-50"
              />
              <span>
                {{ latestDeal.property_code }}
                <template v-if="latestDeal.place_name">· {{ latestDeal.place_name }}</template>
                <template v-if="latestDeal.units">· Unit {{ latestDeal.units }}</template>
                <template v-if="latestDeal.floors">· Floor {{ latestDeal.floors }}</template>
              </span>
            </div>
            <div
              v-if="latestDealClient"
              class="d-flex align-items-center font-small-3 text-muted"
            >
              <feather-icon
                icon="UserIcon"
                size="13"
                class="mr-50"
              />
              <span>{{ latestDealClient }}</span>
            </div>
          </div>

          <div class="d-flex align-items-center mb-2">
            <b-avatar
              variant="light-success"
              size="32"
              class="mr-75"
            >
              <feather-icon
                icon="DollarSignIcon"
                size="16"
              />
            </b-avatar>
            <div>
              <small class="text-muted d-block">Commission Earned</small>
              <span class="font-weight-bold">฿{{ formatCurrency(latestDeal.commission) }}</span>
            </div>
          </div>
        </template>

        <div
          v-else
          class="text-center py-1 mb-2 flex-fill d-flex flex-column align-items-center justify-content-center"
        >
          <feather-icon
            icon="AwardIcon"
            size="28"
            class="text-muted mb-50"
          />
          <p class="text-muted mb-0 font-small-3">
            No closed deals yet.<br>
            Your next win will show up here.
          </p>
        </div>

        <div class="mt-auto">
          <hr class="my-1">

          <div class="d-flex justify-content-between">
            <div class="d-flex align-items-center">
              <feather-icon
                icon="CheckCircleIcon"
                size="15"
                class="text-success mr-50"
              />
              <div>
                <span class="font-weight-bolder d-block">{{ monthlyCount }}</span>
                <small class="text-muted">Deals this month</small>
              </div>
            </div>
            <div class="d-flex align-items-center">
              <feather-icon
                icon="TrendingUpIcon"
                size="15"
                class="text-primary mr-50"
              />
              <div>
                <span class="font-weight-bolder d-block">฿{{ formatCurrency(monthlyCommission) }}</span>
                <small class="text-muted">Commission this month</small>
              </div>
            </div>
          </div>
        </div>
      </template>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard, BCardBody, BAvatar, BLink, BSpinner, BBadge,
} from 'bootstrap-vue'
import store from '@/store'
import { getUserData } from '@/auth/utils'
import closedealStoreModule from '@/views/close-deal/closedealStoreModule'

const CLOSE_DEAL_STORE_MODULE_NAME = 'app-close-deal'

export default {
  components: {
    BCard,
    BCardBody,
    BAvatar,
    BLink,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      name: '',
      latestDeal: null,
      loadingDeal: true,
      monthlyCount: 0,
      monthlyCommission: 0,
    }
  },
  computed: {
    isAdmin() {
      const userData = getUserData()
      return !!(userData && userData.role === 'Admin')
    },
    latestDealClient() {
      if (!this.latestDeal) return ''
      return `${this.latestDeal.client_firstname || ''} ${this.latestDeal.client_lastname || ''}`.trim()
    },
    subtitle() {
      if (this.isAdmin) return 'Welcome to your dashboard'
      if (this.loadingDeal) return ''
      return this.latestDeal ? "You've closed a new deal this month" : "No deals closed this month yet"
    },
    greeting() {
      if (this.isAdmin) return 'Hello'
      if (this.loadingDeal) return 'Congratulations'
      return this.latestDeal ? 'Congratulations' : 'Hello'
    },
    greetingIcon() {
      if (this.isAdmin) return 'SunIcon'
      if (this.loadingDeal) return 'AwardIcon'
      return this.latestDeal ? 'AwardIcon' : 'SunIcon'
    },
    greetingEmoji() {
      if (this.isAdmin) return '👋'
      if (this.loadingDeal) return '🎉'
      return this.latestDeal ? '🎉' : '👋'
    },
  },
  created() {
    const userData = getUserData()
    this.name = userData ? userData.name : ''

    // Admin has no CloseDeal permission — skip the deal fetch/store entirely
    // and show a plain greeting card instead.
    if (this.isAdmin) {
      this.loadingDeal = false
      return
    }

    if (!store.hasModule(CLOSE_DEAL_STORE_MODULE_NAME)) store.registerModule(CLOSE_DEAL_STORE_MODULE_NAME, closedealStoreModule)

    if (!userData) {
      this.loadingDeal = false
      return
    }

    const today = new Date()

    store.dispatch('app-close-deal/fetchData', {
      perpage: 1,
      offset: 0,
      code: '',
      type_id: userData.type_id,
      created_by: userData.id,
      month: today.getMonth() + 1,
      year: today.getFullYear(),
    })
      .then(response => {
        this.latestDeal = response.data.data && response.data.data.length ? response.data.data[0] : null
        this.monthlyCount = response.data.total || 0
        this.monthlyCommission = (response.data.sums && response.data.sums.commission) || 0
      })
      .finally(() => {
        this.loadingDeal = false
      })
  },
  beforeDestroy() {
    if (store.hasModule(CLOSE_DEAL_STORE_MODULE_NAME)) store.unregisterModule(CLOSE_DEAL_STORE_MODULE_NAME)
  },
  methods: {
    formatCurrency(value) {
      return (Number(value) || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })
    },
    formatDate(value) {
      return new Date(value).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    },
  },
}
</script>

<style scoped>
.close-deal-card .card-body {
  padding-bottom: 1.25rem;
}
</style>
